import { useHttpClient } from '@/composables/useHttpClient';
import { useLogError } from '@/composables/useLogError';
import { useSettingsStore } from '@/stores/useSettingsStore';
import { computed, ref, watch, type Ref } from 'vue';

interface Invoice {
  date: Date;
  name: string;
  url: string;
}

class InvoicesState {
  invoices: Invoice[];
  loaded: boolean;

  private constructor(invoices: Invoice[], loaded: boolean) {
    this.invoices = invoices;
    this.loaded = loaded;
  }

  public static of(invoices: Invoice[]) {
    return new InvoicesState(invoices,true);
  }

  public static LOADING: InvoicesState = new InvoicesState([], false);
}

export function useInvoices() {
  const { logError } = useLogError()

  const allInvoices: Ref<InvoicesState> = ref(InvoicesState.LOADING);
  const selectedYear: Ref<number | null> = ref(null)

  const years = computed(() => {
    if(!allInvoices.value.loaded) {
      return [];
    }
    return allInvoices.value.invoices
      .reduce((acc: number[], invoice: Invoice) => {
        const year = invoice.date.getFullYear()
        if (!acc.includes(year)) {
          acc.push(year)
        }

        return acc
      }, [])
      .sort((a, b) => b - a)
  })

  const activeYear = computed(() => selectedYear.value ?? years.value[0])

  const invoicesOfYear = computed(() => {
    if(! allInvoices.value.loaded) {
      return [];
    }
    return allInvoices.value.invoices
      .filter((invoice) => invoice.date.getFullYear() === activeYear.value)
      .sort((a, b) => a.date.getTime() - b.date.getTime())
  })

  function selectYear(year) {
    selectedYear.value = year
  }

  const invoicesLoaded = computed(() => allInvoices.value.loaded)

  async function load() {
    const { reemApi } = useHttpClient()
    const settingsStore = useSettingsStore()
    try {
      const result = await reemApi.get('/efvertragliste', {
        params: {
          fields: 'get_rechnungen_simple,get_dokumenteliste,get_dokument_httplink',
        },
      })

      if (!result || !result.data) {
        throw new Error('Invoices request returned unexpected format.')
      }

      const contractData = settingsStore.contractData;

      const loadInvoices = () => {
        const contractId = contractData.contractId;
        const contract = result.data.list.find((c) => c.vertragsnummer === contractId)

        if (contract) {
          // throw new Error(`No contract found for contractId: ${contractId}`)
          allInvoices.value = InvoicesState.of(contract.get_rechnungen_simple.map((rawInvoice) => ({
            name: rawInvoice.nummer,
            url: rawInvoice.get_dokumenteliste.pop().get_dokument_httplink,
            date: new Date(parseInt(rawInvoice.datum, 10) * 1000),
          })))
        }
      };

      if (contractData.loaded) {
        loadInvoices();
      }

      watch(contractData, (newValue, oldValue) => {
        loadInvoices()
      })
    } catch (err) {
      logError(`Requesting invoices failed: ${err}`)
    }
  }

  return {
    years,
    invoicesOfYear,
    invoicesLoaded,
    activeYear,
    selectYear,
    load,
  }
}
